import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { toast } from 'react-toastify'
import { getWalletTransactions } from 'src/business/services/APIs/CompanyCredits/companyCreditsApi'

const PAGE_SIZE = 20
const MAX_SIZE = 200

/** Convert date input (YYYY-MM-DD) to ISO-8601 start/end of day for API (doc 5.2) */
function toISO8601Start(dateStr) {
  if (!dateStr) return ''
  return `${dateStr}T00:00:00.000Z`
}
function toISO8601End(dateStr) {
  if (!dateStr) return ''
  return `${dateStr}T23:59:59.999Z`
}

export default function WalletTransactions({ onRefresh }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({ content: [], totalElements: 0 })
  const [page, setPage] = useState(0)
  const [transactionType, setTransactionType] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [dateError, setDateError] = useState('')

  const load = useCallback(async () => {
    setDateError('')
    if (from && to && from > to) {
      setDateError('From date must be before or equal to To date')
      toast.error('From date must be before or equal to To date')
      return
    }
    setLoading(true)
    try {
      const params = { page, size: Math.min(PAGE_SIZE, MAX_SIZE), sortBy: 'createdTime', sortDir: 'desc' }
      if (transactionType) params.transactionType = transactionType
      if (from) params.from = toISO8601Start(from)
      if (to) params.to = toISO8601End(to)
      const res = await getWalletTransactions(params)
      if (res?.status === 200 && res?.data) {
        setData({
          content: res.data.content ?? res.data ?? [],
          totalElements: res.data.totalElements ?? (res.data.content?.length ?? 0),
        })
      }
    } finally {
      setLoading(false)
    }
  }, [page, transactionType, from, to])

  useEffect(() => {
    load()
  }, [load])

  const handleFilter = () => {
    if (from && to && from > to) {
      setDateError('From date must be before or equal to To date')
      toast.error('From date must be before or equal to To date')
      return
    }
    setDateError('')
    setPage(0)
    load()
  }

  const rows = data.content || []
  const totalPages = Math.ceil((data.totalElements || 0) / PAGE_SIZE)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setDateError('') }}
          InputLabelProps={{ shrink: true }}
          error={!!dateError}
          helperText={dateError}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={(e) => { setTo(e.target.value); setDateError('') }}
          InputLabelProps={{ shrink: true }}
        />
        <select
          style={{ padding: '8px 12px', borderRadius: 4, minWidth: 120 }}
          value={transactionType}
          onChange={(e) => setTransactionType(e.target.value)}
        >
          <option value="">All types</option>
          <option value="credit">Credit</option>
          <option value="debit">Debit</option>
        </select>
        <Button variant="outlined" onClick={handleFilter}>Apply</Button>
        <Button variant="text" onClick={load}>Refresh</Button>
      </Box>

      {loading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Event</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">No transactions</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, i) => (
                  <TableRow key={row.id || row.createdTime || i}>
                    <TableCell>{row.createdTime ? new Date(row.createdTime).toLocaleString() : '—'}</TableCell>
                    <TableCell>{row.transactionType || '—'}</TableCell>
                    <TableCell>{row.event || '—'}</TableCell>
                    <TableCell align="right">{row.credits != null ? row.credits : '—'}</TableCell>
                    <TableCell>{row.referenceId || row.reference || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {totalPages}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
